import {
  manualPiecesFromPlanApplication,
  manualPiecesToPlanPayload,
} from "../../utils/mapMeasurements";
import { formatPlantingLines, plantingFromPlan } from "./serviceOrderPlanting";
import {
  fertilizationFromPlan,
  formatFertilizationLines,
} from "./serviceOrderFertilization";
import {
  DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT,
  HARVEST_LOSS_SAMPLE_AREA_M2,
  HARVEST_LOSS_SAMPLE_LENGTH_CM,
  HARVEST_LOSS_SAMPLE_WIDTH_CM,
} from "../FieldApp/harvestLossMetrics";

export function createEmptyProductRow() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    dosePerHa: "",
    unit: "L",
  };
}

export function parsePositiveNumber(value) {
  const parsed = Number(String(value || "").replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function parseNonNegativeNumber(value) {
  const parsed = Number(String(value || "").replace(",", "."));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export const DEFAULT_CALIBRATION_TOLERANCE_PERCENT = 5;

export const DEFAULT_HARVEST_LOSS_KG_PER_BAG = 60;
export const DEFAULT_HARVEST_LOSS_MIN_REPETITIONS = 3;

export function productRowsFromPlan(plan) {
  const items = plan && plan.items;
  if (!Array.isArray(items) || items.length === 0) {
    return [createEmptyProductRow()];
  }
  return items.map((item, index) => ({
    id: `plan-item-${index}-${item.name || "row"}`,
    name: item.name || "",
    dosePerHa: item.dose_per_ha > 0 ? String(item.dose_per_ha) : "",
    unit: item.unit || "L",
  }));
}

export function applicationAreaFromPlan(plan) {
  const app = plan && plan.application;
  if (!app || !(app.total_ha > 0)) return null;
  const manualPieces = manualPiecesFromPlanApplication(app);
  const manualHa = manualPieces.reduce(
    (sum, piece) => sum + (Number(piece.areaHa) || 0),
    0
  );
  return {
    totalHa: app.total_ha,
    workHa: app.work_ha,
    fenceHa: app.fence_ha,
    cleanFenceWidthM: app.clean_fence_width_m,
    plotCount: app.plot_count,
    manualPieces,
    manualPieceCount: manualPieces.length,
    manualHa,
  };
}

export function computeProductTotals(productRows, totalHa) {
  const area = parsePositiveNumber(totalHa);
  return (productRows || []).map((row) => {
    const dose = parsePositiveNumber(row.dosePerHa);
    const total = area > 0 && dose > 0 ? dose * area : 0;
    return {
      ...row,
      dose,
      total,
    };
  });
}

export function computeTankMetrics({ sprayVolumePerHa, tankCapacityL, totalHa }) {
  const volume = parsePositiveNumber(sprayVolumePerHa);
  const tank = parsePositiveNumber(tankCapacityL);
  const area = parsePositiveNumber(totalHa);

  if (!volume || !tank || !area) {
    return {
      haPerTank: 0,
      tanksRequired: 0,
      totalSprayVolumeL: 0,
    };
  }

  const haPerTank = tank / volume;
  const totalSprayVolumeL = area * volume;
  const tanksRequired = totalSprayVolumeL / tank;

  return {
    haPerTank,
    tanksRequired,
    totalSprayVolumeL,
  };
}

export function machineIdentsFromPlan(plan) {
  if (!plan) return [];
  if (Array.isArray(plan.machine_idents) && plan.machine_idents.length) {
    return plan.machine_idents
      .map((ident) => String(ident || "").trim())
      .filter(Boolean);
  }
  const single = String(plan.machine_ident || "").trim();
  if (!single) return [];
  if (single.includes(",")) {
    return single
      .split(",")
      .map((ident) => ident.trim())
      .filter(Boolean);
  }
  return [single];
}

export function formatMachineIdentsDisplay(idents) {
  return (idents || [])
    .map((ident) => String(ident || "").trim())
    .filter(Boolean)
    .join(", ");
}

export function harvestLossMonitoringFromPlan(plan) {
  const raw = plan && plan.harvest_loss_monitoring;
  if (!raw || typeof raw !== "object") return null;
  const pmsG = parsePositiveNumber(raw.pms_g);
  if (!pmsG) return null;
  return {
    pmsG,
    toleratedLossKgHa: parseNonNegativeNumber(raw.tolerated_loss_kg_ha),
    kgPerBag: parsePositiveNumber(raw.kg_per_bag) || DEFAULT_HARVEST_LOSS_KG_PER_BAG,
    minRepetitions:
      parsePositiveNumber(raw.min_repetitions) || DEFAULT_HARVEST_LOSS_MIN_REPETITIONS,
    sampleAreaM2: parsePositiveNumber(raw.sample_area_m2) || HARVEST_LOSS_SAMPLE_AREA_M2,
    sampleWidthCm: parsePositiveNumber(raw.sample_width_cm) || HARVEST_LOSS_SAMPLE_WIDTH_CM,
    sampleLengthCm: parsePositiveNumber(raw.sample_length_cm) || HARVEST_LOSS_SAMPLE_LENGTH_CM,
    alertThresholdPercent:
      parseNonNegativeNumber(raw.alert_threshold_percent) ||
      DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT,
    instructions: String(raw.instructions || "").trim(),
  };
}

export function buildHarvestLossMonitoringPayload({
  pmsG,
  toleratedLossKgHa,
  kgPerBag,
  minRepetitions,
  alertThresholdPercent,
}) {
  const pms = parsePositiveNumber(pmsG);
  if (!pms) return null;

  const tolerated = parseNonNegativeNumber(toleratedLossKgHa);
  const bagKg = parsePositiveNumber(kgPerBag) || DEFAULT_HARVEST_LOSS_KG_PER_BAG;
  const repetitions =
    parsePositiveNumber(minRepetitions) || DEFAULT_HARVEST_LOSS_MIN_REPETITIONS;
  const alertThreshold =
    parseNonNegativeNumber(alertThresholdPercent) ||
    DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT;

  return {
    pms_g: pms,
    tolerated_loss_kg_ha: tolerated,
    kg_per_bag: bagKg,
    min_repetitions: repetitions,
    alert_threshold_percent: alertThreshold,
    sample_area_m2: HARVEST_LOSS_SAMPLE_AREA_M2,
    sample_width_cm: HARVEST_LOSS_SAMPLE_WIDTH_CM,
    sample_length_cm: HARVEST_LOSS_SAMPLE_LENGTH_CM,
    instructions:
      `Realize pelo menos três repetições em quadrados de ${HARVEST_LOSS_SAMPLE_WIDTH_CM}×${HARVEST_LOSS_SAMPLE_LENGTH_CM} cm (${HARVEST_LOSS_SAMPLE_AREA_M2.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²) em pontos diferentes do talhão. Conte todas as sementes/grãos encontrados e registre cada amostra no app.`,
  };
}

export function buildServiceOrderPlan({
  productRows,
  applicationArea,
  sprayVolumePerHa,
  tankCapacityL,
  machineIdent,
  machineIdents,
  targetRateLHa,
  calibrationTolerancePercent,
  harvestLossMonitoring,
  planting,
  fertilization,
}) {
  const idents = Array.isArray(machineIdents)
    ? machineIdents.map((ident) => String(ident || "").trim()).filter(Boolean)
    : machineIdent
      ? [String(machineIdent).trim()].filter(Boolean)
      : [];
  const totalHa = parsePositiveNumber(applicationArea && applicationArea.totalHa);
  const items = computeProductTotals(productRows, totalHa).map((row) => ({
    name: String(row.name || "").trim(),
    dose_per_ha: row.dose,
    unit: row.unit || "L",
    total_quantity: row.total,
  }));
  const tankMetrics = computeTankMetrics({
    sprayVolumePerHa,
    tankCapacityL,
    totalHa,
  });
  const sprayVolume = parsePositiveNumber(sprayVolumePerHa);
  const targetRate = parsePositiveNumber(targetRateLHa) || sprayVolume;
  const tolerance = parseNonNegativeNumber(calibrationTolerancePercent);

  const plan = {
    items,
    spray_volume_per_ha: sprayVolume,
    target_rate_l_ha: targetRate,
    calibration_tolerance_percent:
      tolerance > 0 ? tolerance : DEFAULT_CALIBRATION_TOLERANCE_PERCENT,
    tank_capacity_l: parsePositiveNumber(tankCapacityL),
    ha_per_tank: tankMetrics.haPerTank,
    tanks_required: tankMetrics.tanksRequired,
    total_spray_volume_l: tankMetrics.totalSprayVolumeL,
    machine_ident: formatMachineIdentsDisplay(idents),
    machine_idents: idents,
    application: {
      total_ha: totalHa,
      work_ha: parsePositiveNumber(applicationArea && applicationArea.workHa),
      fence_ha: parsePositiveNumber(applicationArea && applicationArea.fenceHa),
      clean_fence_width_m: Number(applicationArea && applicationArea.cleanFenceWidthM) || 0,
      clean_fence_enabled:
        Number(applicationArea && applicationArea.cleanFenceWidthM) > 0,
      plot_count: Number(applicationArea && applicationArea.plotCount) || 0,
      manual_ha: parsePositiveNumber(applicationArea && applicationArea.manualHa),
      manual_piece_count:
        Number(applicationArea && applicationArea.manualPieceCount) || 0,
      manual_pieces: manualPiecesToPlanPayload(
        applicationArea && applicationArea.manualPieces
      ),
    },
  };

  if (harvestLossMonitoring) {
    plan.harvest_loss_monitoring = harvestLossMonitoring;
  }

  if (planting) {
    plan.planting = planting;
  }

  if (fertilization) {
    plan.fertilization = fertilization;
  }

  return plan;
}

export function buildTemplateServiceOrderPlan(plan) {
  if (!plan) return null;

  const items = (plan.items || [])
    .map((item) => ({
      name: String(item.name || "").trim(),
      dose_per_ha: Number(item.dose_per_ha) || 0,
      unit: item.unit || "L",
      total_quantity: 0,
    }))
    .filter((item) => item.name);

  return {
    items,
    spray_volume_per_ha: parsePositiveNumber(plan.spray_volume_per_ha),
    target_rate_l_ha:
      parsePositiveNumber(plan.target_rate_l_ha) ||
      parsePositiveNumber(plan.spray_volume_per_ha),
    calibration_tolerance_percent:
      plan.calibration_tolerance_percent != null
        ? parseNonNegativeNumber(plan.calibration_tolerance_percent)
        : DEFAULT_CALIBRATION_TOLERANCE_PERCENT,
    tank_capacity_l: parsePositiveNumber(plan.tank_capacity_l),
    ha_per_tank: 0,
    tanks_required: 0,
    total_spray_volume_l: 0,
    machine_ident: formatMachineIdentsDisplay(machineIdentsFromPlan(plan)),
    machine_idents: machineIdentsFromPlan(plan),
    application: {
      total_ha: 0,
      work_ha: 0,
      fence_ha: 0,
      clean_fence_width_m: 0,
      clean_fence_enabled: false,
      plot_count: 0,
      manual_ha: 0,
      manual_piece_count: 0,
      manual_pieces: [],
    },
  };
}

export function templatePlanHasRecipeData(plan) {
  if (!plan) return false;
  const hasProducts = (plan.items || []).some((item) =>
    String(item.name || "").trim()
  );
  return (
    hasProducts ||
    parsePositiveNumber(plan.spray_volume_per_ha) > 0 ||
    parsePositiveNumber(plan.target_rate_l_ha) > 0 ||
    parsePositiveNumber(plan.tank_capacity_l) > 0
  );
}

export function formatProductsUsedText(plan, t) {
  if (!plan) return "";

  const lines = [];
  const app = plan.application || {};
  const translate = typeof t === "function" ? t : (value) => value;

  if (app.total_ha > 0) {
    if (app.clean_fence_enabled && app.fence_ha > 0) {
      lines.push(
        `${translate("Área total")}: ${app.total_ha.toFixed(2)} ha (${app.work_ha.toFixed(2)} ha ${translate("talhões")} + ${app.fence_ha.toFixed(2)} ha Cerca limpa @ ${app.clean_fence_width_m} m)`
      );
    } else if (app.manual_ha > 0 && app.work_ha > 0) {
      lines.push(
        `${translate("Área total")}: ${app.total_ha.toFixed(2)} ha (${app.work_ha.toFixed(2)} ha ${translate("talhões")} + ${app.manual_ha.toFixed(2)} ha ${translate("trechos manuais")})`
      );
    } else if (app.manual_ha > 0) {
      lines.push(
        `${translate("Área total")}: ${app.total_ha.toFixed(2)} ha (${app.manual_piece_count || 0} ${translate("trecho(s) manual(is)")})`
      );
    } else {
      lines.push(`${translate("Área total")}: ${app.total_ha.toFixed(2)} ha`);
    }
  }

  if (plan.spray_volume_per_ha > 0) {
    lines.push(
      `${translate("Vazão de calda por hectare")}: ${plan.spray_volume_per_ha.toFixed(2)} L/ha`
    );
  }

  if (plan.target_rate_l_ha > 0) {
    lines.push(
      `${translate("Taxa-alvo (calibragem)")}: ${plan.target_rate_l_ha.toFixed(2)} L/ha`
    );
  }

  if (plan.calibration_tolerance_percent > 0) {
    lines.push(
      `${translate("Tolerância de variação (calibragem)")}: ${plan.calibration_tolerance_percent.toFixed(1)}%`
    );
  }

  if (plan.tank_capacity_l > 0) {
    lines.push(
      `${translate("Tanque")}: ${plan.tank_capacity_l.toFixed(0)} L` +
        (plan.ha_per_tank > 0
          ? ` (${plan.ha_per_tank.toFixed(2)} ha/${translate("tanque")})`
          : "")
    );
  }

  if (plan.tanks_required > 0) {
    lines.push(
      `${translate("Tanques necessários")}: ${plan.tanks_required.toFixed(2)}`
    );
  }

  const machineLine = formatMachineIdentsDisplay(machineIdentsFromPlan(plan));
  if (machineLine) {
    lines.push(`${translate("Máquina(s)")}: ${machineLine}`);
  }

  const planting = plantingFromPlan(plan);
  formatPlantingLines(planting, translate).forEach((line) => lines.push(line));

  const fertilization = fertilizationFromPlan(plan);
  formatFertilizationLines(fertilization, translate).forEach((line) =>
    lines.push(line)
  );

  const harvestLoss = harvestLossMonitoringFromPlan(plan);
  if (harvestLoss) {
    lines.push(
      `${translate("PMS (peso de mil sementes)")}: ${harvestLoss.pmsG.toFixed(1)} g`
    );
    if (harvestLoss.toleratedLossKgHa > 0) {
      lines.push(
        `${translate("Margem de perda tolerada")}: ${harvestLoss.toleratedLossKgHa.toFixed(2)} kg/ha`
      );
      lines.push(
        `${translate("Alerta acima da margem")}: +${harvestLoss.alertThresholdPercent.toFixed(0)}% (${(harvestLoss.toleratedLossKgHa * (1 + harvestLoss.alertThresholdPercent / 100)).toFixed(2)} kg/ha)`
      );
    }
    lines.push(
      `${translate("Peso da saca")}: ${harvestLoss.kgPerBag.toFixed(0)} kg`
    );
    lines.push(
      `${translate("Amostra")}: ${harvestLoss.sampleWidthCm}×${harvestLoss.sampleLengthCm} cm (${harvestLoss.sampleAreaM2.toFixed(2)} m²)`
    );
    lines.push(
      `${translate("Repetições mínimas")}: ${harvestLoss.minRepetitions}`
    );
    if (harvestLoss.instructions) {
      lines.push(`${translate("Instruções")}: ${harvestLoss.instructions}`);
    }
  }

  const productLines = (plan.items || [])
    .filter((item) => item.name)
    .map((item) => {
      const dose =
        item.dose_per_ha > 0 ? `${item.dose_per_ha} ${item.unit}/ha` : "";
      const total =
        item.total_quantity > 0
          ? ` = ${item.total_quantity.toFixed(2)} ${item.unit}`
          : "";
      return `- ${item.name}${dose ? `: ${dose}${total}` : ""}`;
    });

  if (productLines.length) {
    lines.push("", `${translate("Produtos a ser utilizados")}:`, ...productLines);
  }

  return lines.join("\n").trim();
}
